import { useRouter } from '@abyss/web/hooks/useRouter';
import { Box } from '@abyss/web/ui/Box';
import { Button } from '@abyss/web/ui/Button';
import { Heading } from '@abyss/web/ui/Heading';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Layout } from '@abyss/web/ui/Layout';
import { Text } from '@abyss/web/ui/Text';
import { t } from 'i18next';
import { useShallow } from 'zustand/react/shallow';

import { adobeLinkTrackEvent } from '../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { ConstantsRoutes } from '../../../../common/ConstantsRoutes';
import { ContentWrapper } from '../../../../common/ContentWrapper';
import { convertObjectToUrlParams } from '../../../../common/Utils';
import { useInitializeStore } from '../../../../hooks/useInitializeStore';
import { useChipStore } from '../../../../store/useChipStore';
import { ChipState } from '../../../../store/useChipStore/chipStore';
import { useTypeaheadStore } from '../../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../../store/useTypeaheadStore/typeaheadStore';
import {
  BoxStyles,
  ButtonStyles,
  ContainerForNullSections,
  ContentContainer,
  HeadingStyles,
  IconStyles,
  TextStyled,
} from './NullSpecialtyPage.styles';

export type NullPageProps = {
  searchName: string;
};

export const NoSpecialtyResult = ({ searchName }: NullPageProps) => {
  const { navigate } = useRouter();

  const { chipValue: initStoreValue } = useInitializeStore(
    ConstantsRoutes.NULL_SPECIALTY_RESULTS.key
  );

  const { drawerName } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      drawerName: state.typeaheadSearchStore.drawerName,
    }))
  );

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const {
    chipValue: storeChipValue,
    setOpenSpecialtyDrawer,
    setOpenAllSpecialtyDrawer,
  } = chipStore;

  const chipValue = storeChipValue || initStoreValue;
  const referralRequiredDrawerState = () => {
    let state = false;
    if (drawerName === 'referral required specialties') {
      state = true;
      return state;
    }
    return state;
  };

  const AllSpecialtyDrawerState = () => {
    let state = false;
    if (drawerName === 'all specialties') {
      state = true;
      return state;
    }
    return state;
  };
  const handleBackButton = () => {
    adobeLinkTrackEvent({
      name: 'back button',
      location: 'modal:null result page for specialty',
      type: 'internal',
    });

    setOpenSpecialtyDrawer(referralRequiredDrawerState());
    setOpenAllSpecialtyDrawer(AllSpecialtyDrawerState());
    const urlParams = convertObjectToUrlParams(chipStore, {
      chipValue,
    });
    navigate(`/${urlParams}`, { replace: true });
  };

  return (
    <ContainerForNullSections>
      <Box
        color="$white"
        css={BoxStyles}
        data-auto-testid="no-specialty-page"
        data-testid="no-specialty-page"
      >
        <ContentWrapper>
          <ContentContainer>
            <Heading
              css={HeadingStyles}
              data-auto-testid="no-specialty-results-heading"
              data-testid="no-specialty-results-heading"
            >
              {t('NULL_PAGE.NULL_RESULT_FOR_SPECIALTY_HEADING')} {searchName}
            </Heading>
            <Layout.Stack alignItems="left">
              <Text css={TextStyled}>
                {t('NULL_PAGE.NULL_RESULT_FOR_SPECIALTY_TEXT')}
              </Text>
              <ul
                style={{
                  listStyle: 'disc',
                  paddingLeft: '26px',
                  fontWeight: '600',
                }}
              >
                <li>
                  <Text css={TextStyled}>
                    {' '}
                    {t('NULL_PAGE.NULL_RESULT_FOR_SPECIALTY_1')}
                  </Text>
                </li>
                <li>
                  <Text css={TextStyled}>
                    {t('NULL_PAGE.NULL_RESULT_FOR_SPECIALTY_2')}
                  </Text>
                </li>
              </ul>
            </Layout.Stack>
            <Button
              css={ButtonStyles}
              data-auto-testid="back-to-medical-specialists"
              data-testid="back-to-medical-specialists"
              onClick={handleBackButton}
              size="$md"
            >
              {t(`NULL_PAGE.NULL_RESULTS_BACK_BUTTON.${chipValue}`)}
              <IconMaterial
                color="$white"
                css={IconStyles}
                icon="chevron_right"
                isScreenReadable
                size="24px"
              />
            </Button>
          </ContentContainer>
        </ContentWrapper>
      </Box>
    </ContainerForNullSections>
  );
};
